import React, { useState, useEffect, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Ruler, Loader2, Download, Plus, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, fileUrl } from "../lib/apiClient";

const blankEl = () => ({ name: "", width_ft: "", height_ft: "", depth_ft: "", note: "" });

export default function SpecSheetDialog({ open, onOpenChange, imagePath, kind = "interior", title = "Design Sample", subtitle = "", known = {}, concept = null }) {
  const isExt = kind === "exterior";
  const [loading, setLoading] = useState(false);
  const [building, setBuilding] = useState(false);
  const [overall, setOverall] = useState({ width_ft: "", length_ft: "", height_ft: "" });
  const [elements, setElements] = useState([]);
  const [notes, setNotes] = useState("");

  const estimate = useCallback(async () => {
    if (!imagePath) return;
    setLoading(true);
    try {
      const ctx = concept
        ? `${title}. Design concept "${concept.name || ""}". Palette: ${concept.palette || ""}. Key materials/finishes: ${concept.materials || ""}. Include these materials as elements with realistic sizes and note the material/finish in each element's note.`
        : title;
      const { data } = await api.post("/ai/estimate-measurements", {
        image_path: imagePath, kind,
        width: Number(known.width) || 0, length: Number(known.length) || 0, height: Number(known.height) || 0,
        context: ctx,
      });
      const o = data.overall || {};
      setOverall({ width_ft: o.width_ft || "", length_ft: o.length_ft || "", height_ft: o.height_ft || "" });
      setElements((data.elements || []).map((e) => ({
        name: e.name || "", width_ft: e.width_ft || "", height_ft: e.height_ft || "",
        depth_ft: e.depth_ft || "", note: e.note || "",
      })));
      const cnote = concept ? `${concept.name ? concept.name + " — " : ""}${concept.palette ? "palette: " + concept.palette + "; " : ""}${concept.materials ? "materials: " + concept.materials : ""}`.trim() : "";
      setNotes(cnote || data.summary || "");
    } catch {
      toast.error("Could not estimate measurements — you can enter them manually");
      setElements([blankEl()]);
    } finally { setLoading(false); }
  }, [imagePath, kind, known, title, concept]);

  useEffect(() => {
    if (open) {
      setOverall({ width_ft: "", length_ft: "", height_ft: "" });
      setElements([]); setNotes("");
      estimate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, imagePath]);

  const setEl = (i, key, val) => setElements((arr) => arr.map((e, j) => (j === i ? { ...e, [key]: val } : e)));
  const addEl = () => setElements((arr) => [...arr, blankEl()]);
  const removeEl = (i) => setElements((arr) => arr.filter((_, j) => j !== i));

  const num = (v) => (v === "" || v === null || isNaN(Number(v)) ? 0 : Number(v));

  const download = async () => {
    setBuilding(true);
    try {
      const payload = {
        image_path: imagePath, title, subtitle, kind,
        overall: { width_ft: num(overall.width_ft), length_ft: num(overall.length_ft), height_ft: num(overall.height_ft) },
        elements: elements.filter((e) => e.name).map((e) => ({
          name: e.name, width_ft: num(e.width_ft), height_ft: num(e.height_ft), depth_ft: num(e.depth_ft), note: e.note || "",
        })),
        notes,
      };
      const { data } = await api.post("/ai/spec-sheet", payload);
      const res = await api.get(`/files/${data.path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${title.replace(/[^\w-]+/g, "_")}_spec.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Spec sheet downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not build spec sheet");
    } finally { setBuilding(false); }
  };

  const dimLabels = isExt ? ["Width ft", "Depth ft", "Height ft"] : ["Width ft", "Length ft", "Ceiling ft"];
  const dimKeys = ["width_ft", "length_ft", "height_ft"];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0D1117] border-[#21262D] text-[#F0F6FC] max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="spec-sheet-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl">
            <Ruler className="w-5 h-5 text-[#34D399]" /> Measured Sample Spec Sheet
          </DialogTitle>
          <p className="text-[#8B949E] text-sm">AI estimates the measurements from your design — edit anything, then download a clean PDF a builder can produce from.</p>
        </DialogHeader>

        {imagePath && (
          <img src={fileUrl(imagePath)} alt="design" className="w-full h-40 object-cover rounded-lg border border-[#21262D]" data-testid="spec-preview" />
        )}

        {concept && (concept.name || concept.materials) && (
          <div className="rounded-lg border border-[#F59E0B]/30 bg-[#F59E0B]/5 px-3 py-2 text-xs text-[#F59E0B] flex items-center gap-2" data-testid="spec-concept-banner">
            <Sparkles className="w-3.5 h-3.5" /> Auto-filled from concept{concept.name ? `: ${concept.name}` : ""}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-8 text-[#8B949E]" data-testid="spec-estimating">
            <Loader2 className="w-5 h-5 animate-spin text-[#34D399]" /> Estimating measurements…
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-[#8B949E]">Overall size</Label>
                <button onClick={estimate} className="text-[11px] text-[#34D399] hover:underline flex items-center gap-1" data-testid="spec-reestimate">
                  <Sparkles className="w-3 h-3" /> Re-estimate
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {dimLabels.map((lb, i) => (
                  <Input key={lb} type="number" value={overall[dimKeys[i]]} placeholder={lb}
                    onChange={(e) => setOverall((o) => ({ ...o, [dimKeys[i]]: e.target.value }))}
                    className="bg-[#161B22] border-[#30363D]" data-testid={`spec-overall-${dimKeys[i]}`} />
                ))}
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs text-[#8B949E]">Key elements</Label>
                <button onClick={addEl} className="text-[11px] text-[#38BDF8] hover:underline flex items-center gap-1" data-testid="spec-add-element">
                  <Plus className="w-3 h-3" /> Add element
                </button>
              </div>
              <div className="space-y-2">
                {elements.map((el, i) => (
                  <div key={i} className="grid grid-cols-12 gap-1.5 items-center" data-testid={`spec-element-${i}`}>
                    <Input value={el.name} placeholder="Element" onChange={(e) => setEl(i, "name", e.target.value)} className="col-span-4 bg-[#161B22] border-[#30363D] h-9 text-sm" data-testid={`spec-el-name-${i}`} />
                    <Input value={el.width_ft} type="number" placeholder="W" onChange={(e) => setEl(i, "width_ft", e.target.value)} className="col-span-2 bg-[#161B22] border-[#30363D] h-9 text-sm" data-testid={`spec-el-w-${i}`} />
                    <Input value={el.height_ft} type="number" placeholder="H" onChange={(e) => setEl(i, "height_ft", e.target.value)} className="col-span-2 bg-[#161B22] border-[#30363D] h-9 text-sm" data-testid={`spec-el-h-${i}`} />
                    <Input value={el.depth_ft} type="number" placeholder="D" onChange={(e) => setEl(i, "depth_ft", e.target.value)} className="col-span-2 bg-[#161B22] border-[#30363D] h-9 text-sm" data-testid={`spec-el-d-${i}`} />
                    <button onClick={() => removeEl(i)} className="col-span-2 flex items-center justify-center text-[#6E7681] hover:text-[#EF4444]" data-testid={`spec-el-remove-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <Input value={el.note} placeholder="Note (material / finish)" onChange={(e) => setEl(i, "note", e.target.value)} className="col-span-12 bg-[#0D1117] border-[#30363D] h-8 text-xs" data-testid={`spec-el-note-${i}`} />
                  </div>
                ))}
                {elements.length === 0 && <p className="text-xs text-[#6E7681]">No elements yet — add one or re-estimate.</p>}
              </div>
            </div>

            <div>
              <Label className="text-xs text-[#8B949E]">Build notes</Label>
              <Textarea value={notes} rows={2} onChange={(e) => setNotes(e.target.value)}
                placeholder="Materials, finishes, tolerances or instructions for the builder…"
                className="mt-1 bg-[#161B22] border-[#30363D] resize-none" data-testid="spec-notes" />
            </div>

            <Button onClick={download} disabled={building || !imagePath} className="w-full font-semibold text-[#0D1117] bg-[#34D399] hover:bg-[#10B981]" data-testid="spec-download-btn">
              {building ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building PDF…</> : <><Download className="w-4 h-4 mr-2" /> Download Spec Sheet (PDF)</>}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
